import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { attachmentStatuses } from '../../util/attachmentConsts'
import { formatPriceStr } from '../../util/billFormatting'
import { billTypeMap } from '../../util/billingConsts'
import { FlexRow, FlexEndContainer, Divider, CenteredRowContainer, FlexColumn } from '../styles/Containers'
import { ExpandableRowContainer, ActiveRow, InactiveRow, ExpandCollapseIconWrapper, ExpandedContentWrapper } from '../styles/ExpandableRowStyles'
import { BoldBodyText, BoldMedBodyText, MedBodyText } from '../styles/Text'
import AttachmentStatus from '../foiAttachments/AttachmentStatus'
import AssignedUserChip from '../common/AssignedUserChip'
import DeleteFeeDraftButton from '../billActions/DeleteFeeDraftButton'
import Bill from '../bill/Bill'
import EditDraftBillButton from '../bill/EditDraftBillButton'

const Item = styled.div`
  flex-basis: 16%;
`

const LastItem = styled(FlexEndContainer)`
  min-width: 120px;
  flex-basis: 20%;
  justify-content: flex-end;
  text-align: right;
`

const MenuContainer = styled(CenteredRowContainer)`
  height: ${props => `${props.height}px`};
  align-items: center;
`

function DraftBillRow({
  bill,
  users = [],
  onlyApplicationFeeAllowed = false,
  editingBill,
  defaultExpanded = false,
  onBillUpdate = () => {},
  handleUpdateEditingBill = () => {}
}) {
  const containerRef = useRef(null)
  const rowRef = useRef(null)

  const [showDetails, setShowDetails] = useState(defaultExpanded)
  const [height, setHeight] = useState(0)

  const toggleShowDetails = () => setShowDetails(showDetails => !showDetails)

  useEffect(() => {
    if (!defaultExpanded || !rowRef.current?.scrollIntoView) return
    rowRef.current.scrollIntoView({ block: 'nearest' })
  }, [defaultExpanded])

  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const el = entry.target
        setHeight(el.clientHeight)
      }
    })
    resizeObserver.observe(containerRef.current)

    return () => resizeObserver.disconnect()
  }, [showDetails])

  const RowDetailsComponent = showDetails ? ActiveRow : InactiveRow

  return (
    <FlexRow ref={rowRef}>
      <ExpandableRowContainer active={showDetails}>
        <RowDetailsComponent ref={containerRef} onClick={toggleShowDetails} active={showDetails}>
          <Item>
            {bill.assignedTo && (
              <AssignedUserChip
                firstName={bill.assignedTo.firstName}
                lastName={bill.assignedTo.lastName}
                color={bill.assignedTo.chipColor}
                isShort
                labelText="Assigned to"
              />
            )}
          </Item>
          <Item>
            <BoldBodyText>{bill.secondaryId}</BoldBodyText>
          </Item>
          <Item>
            <MedBodyText>{billTypeMap[bill.type]}</MedBodyText>
          </Item>
          <Item>
            <MedBodyText>{formatPriceStr(bill.total)}</MedBodyText>
          </Item>
          <Item>
            <BoldMedBodyText>{format(bill.feeLastModified, 'MMM-dd-yyyy')}</BoldMedBodyText>
            <MedBodyText>{format(bill.feeLastModified, 'hh:mm a')}</MedBodyText>
          </Item>
          <LastItem>
            <AttachmentStatus status={bill.workflowStatus} />
          </LastItem>
          <ExpandCollapseIconWrapper>{showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}</ExpandCollapseIconWrapper>
        </RowDetailsComponent>
        {showDetails && (
          <ExpandedContentWrapper>
            <Divider />
            <Bill bill={bill} users={users} handleUpdateDraft={onBillUpdate} />
          </ExpandedContentWrapper>
        )}
      </ExpandableRowContainer>
      <MenuContainer height={height}>
        <FlexColumn gap="5px">
          <EditDraftBillButton
            bill={bill}
            onlyApplicationFeeAllowed={onlyApplicationFeeAllowed}
            editingBill={editingBill}
            onSuccess={onBillUpdate}
            handleUpdateEditingBill={handleUpdateEditingBill}
          />
          <DeleteFeeDraftButton id={bill.id} secondaryId={bill.secondaryId} onSuccess={onBillUpdate} />
        </FlexColumn>
      </MenuContainer>
    </FlexRow>
  )
}

const userShape = PropTypes.shape({
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  chipColor: PropTypes.string.isRequired
})

DraftBillRow.propTypes = {
  bill: PropTypes.shape({
    id: PropTypes.string.isRequired,
    secondaryId: PropTypes.string.isRequired,
    workflowStatus: PropTypes.oneOf(attachmentStatuses).isRequired,
    type: PropTypes.oneOf(['APPLICATION_FEE', 'FEE_ESTIMATE', 'INVOICE']).isRequired,
    percentDue: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    amountDue: PropTypes.number.isRequired,
    lineItems: PropTypes.array.isRequired,
    feeLastModified: PropTypes.instanceOf(Date),
    modifiedBy: userShape,
    assignedTo: userShape
  }).isRequired,
  editingBill: PropTypes.object,
  users: PropTypes.array,
  onlyApplicationFeeAllowed: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  onBillUpdate: PropTypes.func,
  handleUpdateEditingBill: PropTypes.func
}

export default DraftBillRow
